import { Info, Loader2 } from "lucide-react";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { TruncatedText } from "@/components/ui/truncated-text";
import type {
  BiMatchCategory,
  BiMatchProduct,
} from "@/resources/brand-intelligence/brand-intelligence.api";
import { leafName } from "./categoryLabel";

export type ProductsStatus = "loading" | "error" | "ready";

export type CategoryChoiceRow = Pick<
  BiMatchCategory,
  "leaf_category_id" | "name" | "brand_asins" | "brand_revenue"
>;

interface CategoryChoiceSectionsProps {
  categories: CategoryChoiceRow[];
  checkedIds: ReadonlySet<string>;
  onToggle: (leafCategoryId: string) => void;
  products?: BiMatchProduct[];
  productsStatus: ProductsStatus;
}

const MAX_PRODUCTS = 10;

export const fmtUsd = (n: number | null | undefined): string => {
  if (n == null || !Number.isFinite(n)) return "—";
  if (n >= 1_000_000) return `$${(n / 1_000_000).toFixed(1)}M`;
  if (n >= 1_000) return `$${(n / 1_000).toFixed(0)}K`;
  return `$${Math.round(n)}`;
};

// Human ruling 2026-10-13 (intent.md § Rulings in design): "1 product", every other count "n products".
const productCount = (n: number): string =>
  `${n} ${n === 1 ? "product" : "products"}`;

const visibleProducts = (
  products: BiMatchProduct[],
  checkedIds: ReadonlySet<string>,
): BiMatchProduct[] => {
  const seen = new Set<string>();
  return products
    .filter((p) => checkedIds.has(p.leaf_category_id))
    .filter((p) => (seen.has(p.asin) ? false : (seen.add(p.asin), true)))
    .sort((a, b) => {
      if (a.revenue == null && b.revenue == null)
        return a.asin.localeCompare(b.asin);
      if (a.revenue == null) return 1;
      if (b.revenue == null) return -1;
      return b.revenue - a.revenue || a.asin.localeCompare(b.asin);
    })
    .slice(0, MAX_PRODUCTS);
};

export function CategoryChoiceSections({
  categories,
  checkedIds,
  onToggle,
  products,
  productsStatus,
}: CategoryChoiceSectionsProps) {
  const checkedCount = categories.filter((c) =>
    checkedIds.has(c.leaf_category_id),
  ).length;
  const shown = visibleProducts(products ?? [], checkedIds);

  const productsBody = () => {
    if (checkedIds.size === 0) {
      return (
        <p className="text-sm text-muted-foreground">
          Select a category to see its top products.
        </p>
      );
    }
    if (productsStatus === "loading") {
      return (
        <p className="flex items-center gap-2 text-sm text-muted-foreground">
          <Loader2 className="h-4 w-4 animate-spin" />
          Loading products…
        </p>
      );
    }
    if (productsStatus === "error") {
      return (
        <p className="text-sm text-muted-foreground">
          The products could not be loaded.
        </p>
      );
    }
    if (shown.length === 0) {
      return (
        <p className="text-sm text-muted-foreground">
          No products found in the selected categories.
        </p>
      );
    }
    return (
      <TooltipProvider delayDuration={150}>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
          {shown.map((p) => (
            <div
              key={p.asin}
              data-testid={`category-choice-product-${p.asin}`}
              className="flex items-start gap-3 rounded-sm border border-border p-3"
            >
              {p.image_url ? (
                <img
                  src={p.image_url}
                  alt=""
                  className="h-[var(--product-thumb-lg)] w-[var(--product-thumb-lg)] rounded-sm border bg-card object-contain"
                />
              ) : (
                <div className="h-[var(--product-thumb-lg)] w-[var(--product-thumb-lg)] rounded-sm border bg-card" />
              )}
              <div className="min-w-0 flex-1">
                <TruncatedText lines={2} className="text-sm">
                  {p.title || p.asin}
                </TruncatedText>
                <div className="text-xs text-muted-foreground mt-1">
                  {fmtUsd(p.price)} · {fmtUsd(p.revenue)} revenue
                </div>
              </div>
            </div>
          ))}
        </div>
      </TooltipProvider>
    );
  };

  return (
    <div className="space-y-6 py-2">
      <section>
        <h5 className="flex items-center gap-1.5 text-base font-semibold leading-6 text-foreground">
          Please choose a primary category or niche.
          <TooltipProvider delayDuration={150}>
            <Tooltip>
              <TooltipTrigger asChild>
                <button
                  type="button"
                  aria-label="About primary category or niche"
                  className="inline-flex"
                >
                  <Info className="h-4 w-4 text-muted-foreground cursor-help" />
                </button>
              </TooltipTrigger>
              <TooltipContent className="max-w-xs">
                A niche is a grouping of several primary categories. If you
                choose a niche, data is blended across all of those categories.
              </TooltipContent>
            </Tooltip>
          </TooltipProvider>
        </h5>
        <p className="text-sm text-muted-foreground mt-1 mb-3">
          This sets which market data Brand Intelligence uses for share of
          voice, competitors and trends. You can change the category or niche at
          any time.
        </p>
        <div className="rounded-sm border border-border divide-y divide-border">
          {categories.map((c) => (
            <label
              key={c.leaf_category_id}
              data-testid={`category-choice-row-${c.leaf_category_id}`}
              className="flex items-center gap-3 px-3 py-2.5 cursor-pointer hover:bg-muted/50"
            >
              <Checkbox
                checked={checkedIds.has(c.leaf_category_id)}
                onCheckedChange={() => onToggle(c.leaf_category_id)}
              />
              <span className="min-w-0 flex-1 text-sm text-foreground">
                {leafName(c.name)}
              </span>
              <span className="shrink-0 text-xs text-muted-foreground">
                {productCount(c.brand_asins)}
              </span>
              <span className="shrink-0 w-16 text-right text-sm font-semibold tabular-nums text-foreground">
                {fmtUsd(c.brand_revenue)}
              </span>
            </label>
          ))}
        </div>
        <p
          className="text-xs text-muted-foreground mt-2"
          data-testid="category-choice-counter"
        >
          {checkedCount} of {categories.length} categories selected
        </p>
        {checkedCount > 1 && (
          <Alert variant="info" className="mt-3">
            <Info className="h-4 w-4" aria-hidden />
            <AlertDescription>
              Choosing more than one category creates a niche. The data will be
              blended across all categories in that niche.
            </AlertDescription>
          </Alert>
        )}
      </section>
      <section>
        <h5 className="text-base font-semibold leading-6 text-foreground mb-3">
          Top category/niche products by revenue
        </h5>
        {productsBody()}
      </section>
    </div>
  );
}
